import { applyTenantTerminology, resetTenantTerminology } from '@/plugins/i18n';
import { tenantTerminologyService } from '@/services/tenant-terminology.service';

/**
 * Loads the tenant's terminology overlay once per session and applies it to the live i18n
 * instance. Dispatched from auth/validate (which runs after login AND on every navigation); the
 * `applied` + `loading` guards make repeat/concurrent dispatches no-ops (fetch happens once). Reset
 * on logout so a different tenant signing in on the same page load doesn't inherit the prior terms.
 */

// In development, a `?tenantTerms=<fixture>` URL applies a dev overlay synchronously at boot
// (see util/i18n/terminology/dev-tenant-overlay.js). When present, skip the real fetch so the
// dev preview wins instead of being clobbered by the backend config.
function devOverrideActive() {
  return (
    process.env.NODE_ENV !== 'production' &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('tenantTerms')
  );
}

const state = {
  applied: false,
  loading: false,
};

const actions = {
  async load({ state, commit }) {
    // `auth/validate` runs on every navigation and can fire several times in quick succession on
    // boot. The `loading` flag is committed synchronously (before the await) so concurrent
    // dispatches dedupe — `applied` alone can't, since it's only set after the fetch resolves.
    if (state.applied || state.loading) {
      return;
    }
    if (devOverrideActive()) {
      commit('APPLIED');
      return;
    }
    commit('LOADING');
    try {
      const overlay = await tenantTerminologyService.getOverlay();
      applyTenantTerminology(overlay);
      commit('APPLIED');
    } catch (e) {
      // Reset `loading` (leaving `applied` false) so a later navigation retries; defaults stay in
      // effect meanwhile. Bootstrap terminology must never block the app or surface an error toast.
      commit('LOAD_FAILED');
    }
  },
  reset({ commit }) {
    resetTenantTerminology();
    commit('CLEAR');
  },
  // Force a re-fetch + re-apply regardless of the `applied` guard. Used after an admin saves or
  // resets a term so the running app reflects the change immediately, without a page reload.
  async reload({ state, commit }) {
    if (state.loading) {
      return; // a fetch is already in flight; it will apply the latest overlay
    }
    commit('LOADING');
    try {
      const overlay = await tenantTerminologyService.getOverlay();
      applyTenantTerminology(overlay);
      commit('APPLIED');
    } catch (e) {
      commit('LOAD_FAILED');
    }
  },
};

const mutations = {
  LOADING(state) {
    state.loading = true;
  },
  APPLIED(state) {
    state.applied = true;
    state.loading = false;
  },
  LOAD_FAILED(state) {
    state.loading = false;
  },
  CLEAR(state) {
    state.applied = false;
    state.loading = false;
  },
};

const getters = {
  applied: (state) => state.applied,
  loading: (state) => state.loading,
};

export const terminology = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
